import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';
import { CiCircleInfo } from 'react-icons/ci';

import css from './FieldToggleSwitch.module.css';

const FieldToggleSwitchComponent = props => {
  const {
    rootClassName,
    className,
    switchClassName,
    labelClassName,
    id,
    label,
    input,
    infoText,
    meta,
    onChange,
    showLabelAsDisabled,
    disabled,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;

  // Error styles shown only after touch + invalid
  const hasError = touched && invalid && error;
  const isDisabled = !!disabled;

  const labelClasses = classNames({
    [css.labelDisabled]: showLabelAsDisabled || isDisabled,
    [labelClassName]: !!labelClassName,
  });

  const switchClasses = classNames(
    css.switch,
    {
      [css.switchError]: hasError,
      [css.switchDisabled]: isDisabled,
    },
    switchClassName
  );

  // For a boolean toggle, treat input.value as checked
  const checked = !!input.value;

  const handleChange = e => {
    const next = e.currentTarget.checked;
    input.onChange(next);
    if (onChange) {
      onChange(next);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <label className={switchClasses}>
        <input
          {...rest}
          id={id}
          name={input.name}
          type="checkbox"
          className={css.input}
          checked={checked}
          disabled={isDisabled}
          onChange={handleChange}
          role="switch"
          aria-checked={checked}
          aria-invalid={hasError ? 'true' : 'false'}
        />
        <span className={css.track} aria-hidden="true">
          <span className={css.thumb} />
        </span>
      </label>

      {label ? (
        <label htmlFor={id} className={css.description}>
          {label}
          <span className={css.infoWrap} tabIndex={0} aria-label="Más información">
            <CiCircleInfo className={css.infoIcon} aria-hidden="true" />
            <span className={css.tooltip} role="tooltip">
              {infoText}
            </span>
          </span>
        </label>
      ) : null}

      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping a toggle switch (checkbox)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.switchClassName add more style rules to the switch wrapper
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required
 * @param {ReactNode} props.label
 * @param {string} props.infoText Label that describes the function for the switch component
 * @param {boolean} props.disabled Whether the switch is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @param {(checked: boolean) => void} props.onChange Optional external change handler
 * @returns {JSX.Element} Final Form Field containing a toggle switch
 */
const FieldToggleSwitch = props => {
  return <Field component={FieldToggleSwitchComponent} {...props} />;
};

export default FieldToggleSwitch;
